import type { Font } from "@pdfme/common";

export type PdfmeFontMap = Font;

import robotoRegularUrl from "@fontsource/roboto/files/roboto-latin-400-normal.woff2?url";
import interRegularUrl from "@fontsource/inter/files/inter-latin-400-normal.woff2?url";
import openSansRegularUrl from "@fontsource/open-sans/files/open-sans-latin-400-normal.woff2?url";
import latoRegularUrl from "@fontsource/lato/files/lato-latin-400-normal.woff2?url";
import montserratRegularUrl from "@fontsource/montserrat/files/montserrat-latin-400-normal.woff2?url";
import poppinsRegularUrl from "@fontsource/poppins/files/poppins-latin-400-normal.woff2?url";
import sourceSans3RegularUrl from "@fontsource/source-sans-3/files/source-sans-3-latin-400-normal.woff2?url";
import merriweatherRegularUrl from "@fontsource/merriweather/files/merriweather-latin-400-normal.woff2?url";
import playfairDisplayRegularUrl from "@fontsource/playfair-display/files/playfair-display-latin-400-normal.woff2?url";
import notoSansRegularUrl from "@fontsource/noto-sans/files/noto-sans-latin-400-normal.woff2?url";
import notoSerifRegularUrl from "@fontsource/noto-serif/files/noto-serif-latin-400-normal.woff2?url";

function toHttpUrl(maybeRelative: string): string {
  if (maybeRelative.startsWith("http://") || maybeRelative.startsWith("https://"))
    return maybeRelative;
  return new URL(maybeRelative, window.location.origin).toString();
}

/**
 * Font URLs of the designer (served by this app). Keep font family names stable: they are stored
 * inside template JSON.
 */
function fontUrls(): Font {
  const fonts: Font = {
    Roboto: {
      data: toHttpUrl(robotoRegularUrl),
      fallback: true,
    },
    Inter: {
      data: toHttpUrl(interRegularUrl),
    },
    "Open Sans": {
      data: toHttpUrl(openSansRegularUrl),
    },
    Lato: {
      data: toHttpUrl(latoRegularUrl),
    },
    Montserrat: {
      data: toHttpUrl(montserratRegularUrl),
    },
    Poppins: {
      data: toHttpUrl(poppinsRegularUrl),
    },
    "Source Sans 3": {
      data: toHttpUrl(sourceSans3RegularUrl),
    },
    Merriweather: {
      data: toHttpUrl(merriweatherRegularUrl),
    },
    "Playfair Display": {
      data: toHttpUrl(playfairDisplayRegularUrl),
    },
    "Noto Sans": {
      data: toHttpUrl(notoSansRegularUrl),
    },
    "Noto Serif": {
      data: toHttpUrl(notoSerifRegularUrl),
    },
  };

  return fonts;
}

type LocalFontSpec = { name: string; file: string };

/** Our own (licensed) fonts from public/fonts/local-fonts.json; missing files are left out. */
async function localFonts(): Promise<Font> {
  const out: Font = {};
  let specs: LocalFontSpec[] = [];
  try {
    const res = await fetch(new URL("/fonts/local-fonts.json", window.location.origin));
    if (res.ok) specs = ((await res.json()) as { fonts?: LocalFontSpec[] }).fonts ?? [];
  } catch (e) {
    console.warn("fonts/local-fonts.json not loaded", e);
  }
  await Promise.all(
    specs.map(async (spec) => {
      const other = spec.file.replace(/\.(ttf|otf)$/i, (m) => (m.toLowerCase() === ".ttf" ? ".otf" : ".ttf"));
      for (const file of [spec.file, other]) {
        try {
          const res = await fetch(new URL(`/fonts/${file}`, window.location.origin));
          // the dev server answers unknown paths with index.html
          if (!res.ok || (res.headers.get("content-type") ?? "").includes("text/html")) continue;
          out[spec.name] = { data: await res.arrayBuffer() };
          return;
        } catch {
          // try the other extension
        }
      }
      console.warn(`Font ${spec.name} not loaded (no file fonts/${spec.file})`);
    }),
  );
  return out;
}

let loaded: Promise<PdfmeFontMap> | null = null;

/**
 * Client-side pdfme font registry used by the Designer, with the font files fetched here and
 * passed as data: pdfme 6 refuses to fetch font URLs that are not on a public host (security
 * hardening), which includes this app's own relative / internal URLs. Fonts that can't be loaded
 * (e.g. Myriad not provided) are left out. Loaded once per page.
 */
export function loadPdfmeFonts(): Promise<PdfmeFontMap> {
  loaded ??= (async () => {
    const out: Font = {};
    await Promise.all(
      Object.entries(fontUrls()).map(async ([name, f]) => {
        try {
          const res = await fetch(String(f.data));
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          out[name] = { ...f, data: await res.arrayBuffer() };
        } catch (e) {
          console.warn(`Font ${name} not loaded`, e);
        }
      }),
    );
    return { ...out, ...(await localFonts()) };
  })();
  return loaded;
}
